import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import Button from "@mui/material/Button/Button";
import TextField from "@mui/material/TextField/TextField";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { ColDef, IGetRowsParams } from "ag-grid-community";
import { ICertificationsDto } from "../../../@types/curriculum.type";
import { DatePicker } from "@mui/x-date-pickers/DatePicker/DatePicker";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { zodResolver } from "@hookform/resolvers/zod/dist/zod.js";
import { z } from "zod";
import { ButtonDeleted } from "../../../components/ButtonDeleted";
import { ButtonEdited } from "../../../components/ButtonEdited";

const isDateValid = (date?: Date | null): boolean => {
  return date !== null && date !== undefined && !isNaN(date.getTime());
};

const formatDateToISO = (date: Date | null) => {
  return date ? dayjs(date).toISOString() : "";
};

const certificationSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  certificationDate: z
    .string()
    .nullable()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        return isDateValid(date);
      },
      {
        message: "Data Início inválida",
      }
    ),
});

export type certificationData = z.infer<typeof certificationSchema>;

interface CertificationsProps {
  setValue: React.Dispatch<React.SetStateAction<ICertificationsDto[]>>;
  certificationValue: ICertificationsDto[];
  isEdit: boolean;
}

export const Certification: React.FC<CertificationsProps> = ({
  setValue,
  certificationValue,
  isEdit,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<certificationData>({
    resolver: zodResolver(certificationSchema),
    defaultValues: {
      name: "",
      certificationDate: null,
    },
  });

  const onDelete = async (index: string | undefined) => {
    if (index) {
      const i = Number(index);
      if (!isNaN(i)) {
        setValue((prev) => {
          const updated = [...prev];
          updated.splice(i, 1);
          return updated;
        });
      }
    }
  };

  const onDeleteRef = useRef<(index: string | undefined) => Promise<void>>(
    async () => {}
  );
  useEffect(() => {
    onDeleteRef.current = onDelete;
  });

  const columnsDefault: ColDef[] = [
    {
      field: "id",
      headerName: "ID",
      hide: true,
    },
    {
      field: "description",
      headerName: "Certificado",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "certificationDate",
      headerName: "Data",
      flex: 1,
      maxWidth: 200,
      cellRenderer: (params: CustomCellRendererProps) => {
        return params.value ? dayjs(params.value).format("MM/YYYY") : "";
      },
    },
    {
      field: "action",
      headerName: "Ação",
      flex: 0.5,
      minWidth: 70,
      filter: false,
      sortable: false,
      hide: !isEdit,
      cellRenderer: (params: CustomCellRendererProps) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: ".5rem",
            }}
          >
            {params && params.data && params.data.id && (
              <ButtonEdited
                handleEditeItem={() => editCertification(params.data.id)}
              />
            )}
            <ButtonDeleted
              handleDeleteItem={() => onDeleteRef.current(params.node.id)}
            />
          </div>
        );
      },
    },
  ];
  const [columns, setColumns] = useState(columnsDefault);

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      try {
        const response = certificationValue;

        if (response) params.successCallback(response, response.length);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  const addOrEditCertification = async (data: any) => {
    if (data) {
      const certification: ICertificationsDto = {
        id: data.id,
        description: data.name || null,
        certificationDate: data.certificationDate
          ? formatDateToISO(
              dayjs(data.certificationDate).tz("America/Sao_Paulo").toDate()
            )
          : "",
      };
      if (certification.id == undefined || certification.id == "") {
        setValue((prev) => [...prev, certification]);
      } else {
        if (certificationValue) {
          setValue((prev) => [
            ...prev.map((x) => (x.id == certification.id ? certification : x)),
          ]);
        }
      }
      reset({
        id: undefined,
        name: "",
        certificationDate: "",
      });
    }
  };

  function editCertification(id: string) {
    const certification = certificationValue.find((x) => x.id == id);
    if (certification) {
      reset({
        id: certification.id,
        name: certification.description,
        certificationDate: certification.certificationDate,
      });
    }
  }

  useEffect(() => {
    setColumns(columnsDefault);
  }, [isEdit]);

  return (
    <div className={styles.certification}>
      {isEdit && (
        <form
          className={styles.formProject}
          onSubmit={handleSubmit(addOrEditCertification)}
        >
          <div className={styles.fields}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  label="Nome do Certificado"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                />
              )}
            />
            <Controller
              name="certificationDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Data do Certificado"
                  value={
                    field.value
                      ? dayjs(field.value).tz("America/Sao_Paulo")
                      : null
                  }
                  views={["year", "month"]}
                  onChange={(newValue) => {
                    field.onChange(
                      newValue ? newValue.startOf("month").toISOString() : null
                    );
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      InputLabelProps: { shrink: true },
                      error: !!errors.certificationDate,
                      helperText: errors.certificationDate?.message,
                    },
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              // disabled={!isEnabled || loading}
              className={styles.btnsSave}
            >
              +
            </Button>
          </div>
        </form>
      )}
      <div className={`${styles.gridContainer}`}>
        <AgGridReact
          ref={gridRef}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          columnDefs={columns}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: false,
            editable: false,
            filter: false,
            floatingFilter: false,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          rowModelType="infinite"
          datasource={getDataSource()}
          animateRows={true}
          infiniteInitialRowCount={10}
        />
      </div>
    </div>
  );
};
